import React from "react";
import { descargarArchivoRepuestos } from "../api";
import { errMsg, fmtMoney, fmtDate, fmtDateTime } from "../utils";

/* Piezas comunes del portal de proveedores de repuestos. */

// Estado → etiqueta corta (como la ve el proveedor) y clase del chip.
// El texto largo del API (status_label) se muestra en el detalle.
export const REP_STATUS = {
  AUTORIZADA: { label: "Nueva", cls: "rep-st-new" },
  RECIBIDA: { label: "Por facturar", cls: "rep-st-rec" },
  FACTURADA: { label: "En revisión", cls: "rep-st-fac" },
  DEVUELTA: { label: "Devuelta", cls: "rep-st-dev" },
  APROBADA: { label: "Aprobada", cls: "rep-st-apr" },
  ANULADA: { label: "Anulada", cls: "rep-st-anu" },
};

export const MB = 1024 * 1024;
export const MAX_XML = 3 * MB;
export const MAX_PDF = 10 * MB;

export function StatusChip({ a }) {
  const st = REP_STATUS[a?.status] || { label: a?.status_label || a?.status || "—", cls: "" };
  return (
    <span className={`chip ${st.cls}`} title={a?.status_label || ""}>
      {st.label}
    </span>
  );
}

/** Bus → "SSR123 · interno 123". */
export function vehLabel(v) {
  if (!v) return "—";
  const parts = [];
  if (v.plate) parts.push(v.plate);
  if (v.internal) parts.push(`interno ${v.internal}`);
  return parts.join(" · ") || "—";
}

/** Sede del proveedor → "EDS Yopal · YOPAL" (y la dirección si se pide). "" si no hay sede. */
export function sedeLabel(b, conDireccion) {
  if (!b) return "";
  const parts = [b.name, b.city];
  if (conDireccion) parts.push(b.address);
  return parts.filter(Boolean).join(" · ");
}

/** Dinero o "—" si no viene. */
export function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return fmtMoney(v);
}

export function fmtSize(bytes) {
  const n = Number(bytes || 0);
  if (n >= MB) return `${(n / MB).toLocaleString("es-CO", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** Error de subida: el 413 lo responde el servidor web (sin "detail"). */
export function uploadErr(e, fallback) {
  if (e?.response?.status === 413) {
    return (
      "Los archivos pesan demasiado para enviarlos. Máximo: XML 3 MB y PDF o foto 10 MB. " +
      "Si es una foto, tómala con menor resolución o envíala en PDF."
    );
  }
  return errMsg(e, fallback);
}

function extFromType(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("xml")) return "xml";
  if (t.includes("jpeg") || t.includes("jpg")) return "jpg";
  if (t.includes("png")) return "png";
  return "pdf";
}

/** Descarga el XML o el PDF/foto de la factura de una autorización. */
export async function bajarArchivo(a, tipo) {
  const { blob, filename } = await descargarArchivoRepuestos(a.id, tipo);
  const ext = tipo === "xml" ? "xml" : extFromType(blob?.type);
  const name = filename || `${a.number || "autorizacion"}-factura.${ext}`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function Row({ k, children }) {
  return (
    <div className="v-row">
      <span>{k}</span>
      <b>{children}</b>
    </div>
  );
}

function withNit(name, nit) {
  if (!name && !nit) return "—";
  return `${name || "—"}${nit ? ` · NIT ${nit}` : ""}`;
}

/** Datos de una factura (la leída del XML o la ya enviada). */
export function FacturaRows({ inv, saved }) {
  if (!inv) return null;
  const showParties = !saved || inv.is_electronic || inv.issuer_name || inv.buyer_name;
  return (
    <div className="v-rows rep-rows">
      {saved && (
        <Row k="Tipo">
          {inv.is_electronic ? "Factura electrónica" : "Factura no electrónica (PDF o foto)"}
        </Row>
      )}
      <Row k="Número">{inv.number || "—"}</Row>
      <Row k="Fecha">{inv.date ? fmtDate(inv.date) : "—"}</Row>
      {showParties && <Row k="Emisor (quien factura)">{withNit(inv.issuer_name, inv.issuer_nit)}</Row>}
      {showParties && <Row k="Adquiriente (a nombre de)">{withNit(inv.buyer_name, inv.buyer_nit)}</Row>}
      <Row k="Subtotal">{money(inv.subtotal)}</Row>
      <Row k="IVA">{money(inv.tax)}</Row>
      <Row k="Total">{money(inv.total)}</Row>
      {(inv.cufe || (!saved && inv.is_electronic !== false)) && (
        <Row k="CUFE">
          {inv.cufe ? <code className="rep-mono">{inv.cufe}</code> : "—"}
        </Row>
      )}
      {saved && inv.uploaded_at && (
        <Row k="Enviada">
          {fmtDateTime(inv.uploaded_at)}
          {inv.uploaded_by_name ? ` · ${inv.uploaded_by_name}` : ""}
        </Row>
      )}
    </div>
  );
}

const CHECK_ICO = { ok: "✓", warning: "!", error: "✕" };

/** Lista de validaciones con ícono verde / amarillo / rojo. */
export function Checks({ checks }) {
  if (!Array.isArray(checks) || checks.length === 0) return null;
  return (
    <ul className="rep-checks">
      {checks.map((c, i) => {
        const st = CHECK_ICO[c?.status] ? c.status : "warning";
        return (
          <li key={c?.code || i} className={`rep-check ${st}`}>
            <span className="rep-check-ico" aria-hidden="true">{CHECK_ICO[st]}</span>
            <div>
              <div className="rep-check-label">{c?.label || c?.code || "Validación"}</div>
              {c?.detail && <div className="rep-check-detail">{c.detail}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function countChecks(checks, status) {
  return Array.isArray(checks) ? checks.filter((c) => c?.status === status).length : 0;
}
